/**
 * HELPERS PARA CLASIFICACIÓN DE PAGOS POR MES
 * Lógica: Compara mes/año de pago vs mes/año actual
 */

/**
 * Obtiene año y mes actual como objeto {year, month}
 */
const getCurrentYearMonth = () => {
	const today = new Date();
	return {
		year: today.getFullYear(),
		month: today.getMonth(), // 0-11
	};
};

/**
 * Obtiene año y mes de una fecha como objeto {year, month}
 */
const getPaymentYearMonth = (date) => {
	const paymentDate = new Date(date);
	return {
		year: paymentDate.getFullYear(),
		month: paymentDate.getMonth(), // 0-11
	};
};

/**
 * Compara dos períodos {year, month}
 * Retorna: -1 si primero < segundo, 0 si iguales, 1 si primero > segundo
 */
const compareYearMonth = (period1, period2) => {
	if (period1.year !== period2.year) {
		return period1.year < period2.year ? -1 : 1;
	}
	if (period1.month !== period2.month) {
		return period1.month < period2.month ? -1 : 1;
	}
	return 0;
};

/**
 * Clasifica un pago basado en su mes vs mes actual
 * Retorna: 'pagado' | 'deuda' | 'corriente' | 'futuro'
 *
 * - PAGADO: status === "pagado" (sin importar fecha)
 * - DEUDA: status === "pendiente" AND fecha_mes < mes_actual
 * - CORRIENTE: status === "pendiente" AND fecha_mes === mes_actual
 * - FUTURO: status === "pendiente" AND fecha_mes > mes_actual
 */
export const classifyPayment = (pago) => {
	// Si ya está pagado, no importa la fecha
	if (pago.status === "pagado") {
		return "pagado";
	}

	// Si no está pagado, comparar fechas
	if (pago.status === "pendiente") {
		const currentPeriod = getCurrentYearMonth();
		const paymentPeriod = getPaymentYearMonth(pago.date);
		const comparison = compareYearMonth(paymentPeriod, currentPeriod);

		if (comparison < 0) {
			return "deuda"; // Pago anterior a mes actual
		}

		if (comparison === 0) {
			return "corriente"; // Pago en mes actual (aunque sea 1ro del mes)
		}

		if (comparison > 0) {
			return "futuro"; // Pago posterior a mes actual
		}
	}

	return "desconocido";
};

/**
 * Calcula estadísticas completas de pagos clasificados
 * 
 * Retorna:
 * - pagado: {count, total}
 * - deuda: {count, total}
 * - corriente: {count, total}
 * - futuro: {count, total}
 * - totalPagado, totalAdeudado, totalFuturo, totalGeneral
 * - pagarAhoraPara: deuda + corriente (para ponerse al corriente)
 * - pendienteLuego: futuro (lo que pagará después)
 * - porcentajes
 */
export const calculatePaymentStats = (pagos) => {
	if (!pagos || pagos.length === 0) {
		return {
			pagado: { count: 0, total: 0 },
			deuda: { count: 0, total: 0 },
			corriente: { count: 0, total: 0 },
			futuro: { count: 0, total: 0 },
			totalPagado: 0,
			totalAdeudado: 0, // deuda + corriente
			totalFuturo: 0,
			totalGeneral: 0,
			pagarAhoraPara: 0, // Para ir al corriente
			pendienteLuego: 0, // Futuro
			percentPagado: 0,
			percentAdeudado: 0,
			percentFuturo: 0,
			hayDeuda: false,
			hayAtraso: false,
		};
	}

	const stats = {
		pagado: { count: 0, total: 0 },
		deuda: { count: 0, total: 0 },
		corriente: { count: 0, total: 0 },
		futuro: { count: 0, total: 0 },
	};

	// Clasificar cada pago
	pagos.forEach((pago) => {
		const clasificacion = classifyPayment(pago);
		stats[clasificacion].count += 1;
		stats[clasificacion].total += pago.payment;
	});

	// Calcular totales
	const totalPagado = stats.pagado.total;
	const totalAdeudado = stats.deuda.total + stats.corriente.total; // Deuda + Corriente
	const totalFuturo = stats.futuro.total;
	const totalGeneral = totalPagado + totalAdeudado + totalFuturo;

	// Calcular porcentajes
	const percentPagado = totalGeneral > 0 ? Math.round((totalPagado / totalGeneral) * 100) : 0;
	const percentAdeudado = totalGeneral > 0 ? Math.round((totalAdeudado / totalGeneral) * 100) : 0;
	const percentFuturo = totalGeneral > 0 ? Math.round((totalFuturo / totalGeneral) * 100) : 0;

	const hayDeuda = stats.deuda.total > 0;
	const hayAtraso = stats.deuda.count > 0;

	return {
		...stats,
		totalPagado,
		totalAdeudado, // Si paga ahora, paga deuda + corriente
		totalFuturo, // Lo que pagará después
		totalGeneral,
		pagarAhoraPara: totalAdeudado, // Monto para ir al corriente
		pendienteLuego: totalFuturo, // Pendiente diferente (futuro)
		percentPagado,
		percentAdeudado,
		percentFuturo,
		hayDeuda, // Boolean para saber si hay deuda
		hayAtraso, // Boolean para saber si hay atrasos
	};
};

/**
 * Retorna objeto con información detallada para mostrar al usuario
 */
export const getPaymentStatusSummary = (stats) => {
	return {
		// Información sobre lo pagado
		pagadoInfo: {
			label: "Pagado",
			count: stats.pagado.count,
			total: stats.pagado.total,
			icon: "✓",
			color: "green",
		},
		// Información sobre deuda (vencida)
		deudaInfo: {
			label: "Deuda (Vencidos)",
			count: stats.deuda.count,
			total: stats.deuda.total,
			icon: "⏰",
			color: "red",
		},
		// Información sobre corriente (este mes)
		corrienteInfo: {
			label: "Corriente (Este Mes)",
			count: stats.corriente.count,
			total: stats.corriente.total,
			icon: "📅",
			color: "yellow",
		},
		// Información sobre futuro (próximos meses)
		futuroInfo: {
			label: "Futuro",
			count: stats.futuro.count,
			total: stats.futuro.total,
			icon: "📆",
			color: "blue",
		},
		// Lo importante para el usuario
		accionesRequeridas: {
			pagarAhoraPara: stats.pagarAhoraPara,
			mensaje: stats.pagarAhoraPara > 0 
				? `Si paga ahora, paga: ${formatCurrency(stats.pagarAhoraPara)} para ponerse al corriente`
				: "Estás al corriente",
			pendienteLuego: stats.pendienteLuego,
			mensajeFuturo: `Pendiente después: ${formatCurrency(stats.pendienteLuego)}`,
		},
	};
};

/**
 * Formatea moneda a peso mexicano
 */
const formatCurrency = (value) => {
	return new Intl.NumberFormat("es-MX", {
		style: "currency",
		currency: "MXN",
	}).format(value);
};

/**
 * Retorna badge HTML para mostrar clasificación de un pago
 */
export const getPaymentClassificationBadge = (pago) => {
	const classification = classifyPayment(pago);
	
	const badges = {
		pagado: {
			bg: "bg-green-100",
			text: "text-green-800",
			label: "Pagado",
			icon: "✓",
		},
		deuda: {
			bg: "bg-red-100",
			text: "text-red-800",
			label: "Deuda",
			icon: "⏰",
		},
		corriente: {
			bg: "bg-yellow-100",
			text: "text-yellow-800",
			label: "Corriente",
			icon: "📅",
		},
		futuro: {
			bg: "bg-blue-100",
			text: "text-blue-800",
			label: "Futuro",
			icon: "📆",
		},
	};

	return badges[classification] || badges.futuro;
};

/**
 * Función útil para testing: muestra la clasificación de un pago
 */
export const debugPaymentClassification = (pago) => {
	const current = getCurrentYearMonth();
	const paymentPeriod = getPaymentYearMonth(pago.date);
	const classification = classifyPayment(pago);
	
	console.log(`Pago $${pago.payment}:`, {
		fecha: new Date(pago.date).toLocaleDateString("es-MX"),
		status: pago.status,
		periodoActual: `${current.month + 1}/${current.year}`,
		periodoPago: `${paymentPeriod.month + 1}/${paymentPeriod.year}`,
		clasificacion: classification,
	});
};

export default {
	classifyPayment,
	calculatePaymentStats,
	getPaymentStatusSummary,
	getPaymentClassificationBadge,
	debugPaymentClassification,
};